/**
 * `/audits/:id/review` — review and submit (`AuditReview.dc.html`, §7.2).
 *
 * The blocking rules are computed server-side and shown verbatim. The screen never
 * re-derives them: what it lists is exactly what submit will enforce, so a green screen
 * followed by a rejected submit is impossible.
 *
 * Two states carry the weight. **Submit** is refused while anything blocking remains,
 * with the refusal explained per rule and the offending refs named. **Approve** is
 * refused for whoever submitted — enforced in the controller, so this screen shows the
 * reason rather than hiding the button.
 */

import { useState } from 'react';
import type { CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { ScoreRing, SectionBar, maturityLabel } from '@portal/ui';
import type { Review } from '@portal/contracts';

import { ApiClientError, api } from '../api.js';

const LABEL: CSSProperties = {
  fontSize: 'var(--fs-label)',
  fontWeight: 600,
  letterSpacing: 'var(--tracking-label)',
  textTransform: 'uppercase',
  color: 'var(--mu)',
};

const CARD: CSSProperties = {
  background: 'var(--sf)',
  border: '1px solid var(--bd)',
  borderRadius: 'var(--r-lg)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const NOTE: CSSProperties = { fontSize: 11, color: 'var(--mu)', lineHeight: 1.5 };
const MONO: CSSProperties = { fontFamily: 'var(--font-mono)' };

/** Rule codes the server sends, mapped to what an auditor should do about them. */
const RULE_HINT: Record<string, string> = {
  PENDING_MANDATORY: 'Answer them, mark them not applicable, or raise a waiver.',
  REJECTED_WITHOUT_FINDING: 'A rejected mandatory item needs a finding recording what is wrong.',
  MISSING_NA_JUSTIFICATION: 'Not-applicable needs a reason. The database will not store it blank.',
  MISSING_EVIDENCE: 'Attach an artefact, or attest in writing under your own name.',
  WAIVER_NOT_APPROVED: 'The waiver needs its second signature before this counts as accepted.',
  WAIVER_EXPIRED: 'Renew the waiver or answer the item.',
  STALE_VERIFICATION: 'The freshness window has closed — re-verify rather than carrying it.',
};

function RuleList({
  rows,
  tone,
  emptyText,
}: {
  readonly rows: Review['blocking'];
  readonly tone: 'blocking' | 'advisory';
  readonly emptyText: string;
}) {
  const colour = tone === 'blocking' ? 'var(--ba)' : 'var(--wa)';
  const bg = tone === 'blocking' ? 'var(--bab)' : 'var(--wab)';
  const bd = tone === 'blocking' ? 'var(--ba-bd)' : 'var(--wa-bd)';

  if (rows.length === 0) {
    return (
      <div style={{ padding: '12px 13px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ ...NOTE, color: 'var(--ok)' }}>{emptyText}</span>
      </div>
    );
  }

  return (
    <>
      {rows.map((row) => (
        <div
          key={row.rule}
          style={{
            padding: '10px 13px',
            borderBottom: '1px solid var(--bd-soft)',
            display: 'flex',
            flexDirection: 'column',
            gap: 5,
            background: bg,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ ...MONO, fontSize: 10, fontWeight: 600, color: colour }}>
              {row.rule}
            </span>
            <span
              style={{
                ...MONO,
                fontSize: 10,
                color: colour,
                border: `1px solid ${bd}`,
                borderRadius: 'var(--r-sm)',
                padding: '1px 5px',
                marginLeft: 'auto',
              }}
            >
              {row.refs.length} {row.refs.length === 1 ? 'item' : 'items'}
            </span>
          </div>
          <span style={{ fontSize: 12, color: 'var(--tx)' }}>{row.detail}</span>
          {RULE_HINT[row.rule] !== undefined && (
            <span style={{ ...NOTE, color: colour }}>{RULE_HINT[row.rule]}</span>
          )}
          <span style={{ ...MONO, fontSize: 10.5, color: 'var(--mu)', lineHeight: 1.6 }}>
            {row.refs.slice(0, 24).join(' · ')}
            {row.refs.length > 24 && ` … +${String(row.refs.length - 24)}`}
          </span>
        </div>
      ))}
    </>
  );
}

export function AuditReviewRoute() {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [approved, setApproved] = useState<{ number: number; closedAt: string } | null>(null);

  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me() });
  const review = useQuery({ queryKey: ['audit-review', id], queryFn: () => api.auditReview(id) });

  const submit = useMutation({
    mutationFn: () => api.submitAudit(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['audit-review', id] });
      void queryClient.invalidateQueries({ queryKey: ['audit', id] });
    },
  });

  const approve = useMutation({
    mutationFn: () => api.approveAudit(id),
    onSuccess: (result) => {
      setApproved({ number: result.number, closedAt: result.closedAt });
      void queryClient.invalidateQueries({ queryKey: ['audit-review', id] });
      void queryClient.invalidateQueries({ queryKey: ['portfolio'] });
    },
  });

  const cancel = useMutation({
    mutationFn: () => api.cancelAudit(id, null),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['audit-review', id] });
      void queryClient.invalidateQueries({ queryKey: ['audit', id] });
      void queryClient.invalidateQueries({ queryKey: ['portfolio'] });
    },
  });

  if (review.isPending) {
    return <div style={{ padding: 24, ...NOTE }}>Running the blocking rules…</div>;
  }

  if (review.isError) {
    return (
      <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <strong style={{ color: 'var(--ba)' }}>
          {review.error instanceof ApiClientError
            ? review.error.message
            : 'The review could not be loaded.'}
        </strong>
        <Link to="/" style={{ fontSize: 12.5 }}>
          Back to the portfolio
        </Link>
      </div>
    );
  }

  const data = review.data;
  const closed = data.status === 'CLOSED';
  const inReview = data.status === 'IN_REVIEW';
  const cancelled = data.status === 'CANCELLED' || data.status === 'ARCHIVED';
  const percent = data.projected.mandatory.percentRounded;

  return (
    <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <header style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--mu)' }}
          >
            <Link to={`/audits/${id}`}>Workspace</Link>
            <span>›</span>
            <Link to={`/audits/${id}/evidence`}>Evidence catalog</Link>
            <span>›</span>
            <span style={{ color: 'var(--tx)', fontWeight: 600 }}>Review &amp; submit</span>
          </div>
          <h1 style={{ margin: 0, font: '600 21px/1.1 var(--font-sans)', letterSpacing: '-0.01em' }}>
            Review &amp; submit
          </h1>
          <span style={NOTE}>
            These are the rules the server will enforce, not a second opinion computed here.
          </span>
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14 }}>
          <ScoreRing
            percent={percent}
            size={76}
            caption={closed ? 'FROZEN %' : 'PROJECTED %'}
            maturity={data.projected.maturity}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 220 }}>
            <span style={{ ...LABEL, fontSize: 9.5 }}>
              {closed ? 'frozen at approval' : 'if closed as it stands'}
            </span>
            <span style={NOTE}>
              {data.projected.maturity === null
                ? 'No applicable mandatory items in scope.'
                : maturityLabel(data.projected.maturity)}
              {' · '}
              {data.projected.mandatory.verified} of {data.projected.mandatory.applicable} mandatory
              verified
            </span>
            {data.projected.verifiedByAttestation > 0 && (
              <span style={NOTE}>
                <b style={{ color: 'var(--tx)' }}>{data.projected.verifiedByAttestation}</b> of them
                rest on an attestation rather than an artefact.
              </span>
            )}
          </div>
        </div>
      </header>

      {/* the approved banner — the only place a frozen score is announced */}
      {approved !== null && (
        <div
          style={{
            ...CARD,
            borderColor: 'var(--ok-bd)',
            background: 'var(--okb)',
            padding: '12px 14px',
            flexDirection: 'row',
            gap: 12,
            alignItems: 'center',
          }}
        >
          <strong style={{ fontSize: 13, color: 'var(--ok)' }}>
            Audit #{approved.number} is closed
          </strong>
          <span style={{ ...NOTE, color: 'var(--ok)' }}>
            Approved {approved.closedAt.slice(0, 10)}. The score is written once and cannot be
            recomputed — nothing in this audit can be edited again.
          </span>
          <Link to={`/audits/${id}`} style={{ marginLeft: 'auto', fontSize: 12.5 }}>
            Open read-only
          </Link>
        </div>
      )}

      {/* resolution */}
      <div style={{ ...CARD, padding: '12px 14px', gap: 9 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={LABEL}>Resolution</span>
          <span style={{ ...MONO, fontSize: 12, fontWeight: 600 }}>
            {data.resolution.resolved} / {data.resolution.inScope}
          </span>
          {data.resolution.pending > 0 && (
            <span style={{ ...NOTE, color: 'var(--wa)' }}>
              {data.resolution.pending} still unanswered
            </span>
          )}
          <span style={{ ...NOTE, marginLeft: 'auto' }}>
            Status: <b style={{ color: 'var(--tx)' }}>{data.status.replace('_', ' ').toLowerCase()}</b>
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {data.projected.sections.map((section) => (
            <div key={section.section} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <span style={{ ...MONO, fontSize: 11, fontWeight: 600, width: 13 }}>
                {section.section}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <SectionBar counts={section.counts} />
              </div>
              <span
                style={{
                  ...MONO,
                  fontSize: 11,
                  width: 42,
                  textAlign: 'right',
                  color: section.mandatory.percentRounded === null ? 'var(--mu)' : 'var(--tx)',
                }}
              >
                {section.mandatory.percentRounded === null
                  ? '—'
                  : `${String(section.mandatory.percentRounded)}%`}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
        {/* blocking + advisory */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ ...CARD, ...(data.blocking.length > 0 ? { borderColor: 'var(--ba-bd)' } : {}) }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 9,
                padding: '9px 13px',
                borderBottom: '1px solid var(--bd)',
              }}
            >
              <strong style={{ fontSize: 12.5 }}>Blocking</strong>
              <span
                style={{
                  ...MONO,
                  fontSize: 10.5,
                  color: data.blocking.length > 0 ? 'var(--ba)' : 'var(--ok)',
                }}
              >
                {data.blocking.length}
              </span>
              <span style={{ ...NOTE, marginLeft: 'auto' }}>Submit is refused while any remain</span>
            </div>
            <RuleList
              rows={data.blocking}
              tone="blocking"
              emptyText="Nothing blocking. Every mandatory item in scope is resolved."
            />
          </div>

          <div style={CARD}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 9,
                padding: '9px 13px',
                borderBottom: '1px solid var(--bd)',
              }}
            >
              <strong style={{ fontSize: 12.5 }}>Advisory</strong>
              <span style={{ ...MONO, fontSize: 10.5, color: 'var(--mu)' }}>
                {data.advisory.length}
              </span>
              <span style={{ ...NOTE, marginLeft: 'auto' }}>
                Worth fixing; will not stop a submit
              </span>
            </div>
            <RuleList
              rows={data.advisory}
              tone="advisory"
              emptyText="Nothing advisory either."
            />
          </div>
        </div>

        {/* the two actions */}
        <div style={{ width: 330, flex: '0 0 330px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ ...CARD, padding: '13px 14px', gap: 10 }}>
            <span style={LABEL}>Submit for review</span>

            {cancelled ? (
              <span style={NOTE}>This audit was cancelled and can no longer be submitted.</span>
            ) : closed ? (
              <span style={NOTE}>
                This audit is closed. Its score was frozen at approval and there is nothing left to
                submit.
              </span>
            ) : inReview ? (
              <span style={{ ...NOTE, color: 'var(--ac)' }}>
                Already submitted and waiting for approval. Answers are locked while it is in
                review.
              </span>
            ) : (
              <>
                <span style={NOTE}>
                  Moves the audit to <b style={{ color: 'var(--tx)' }}>in review</b> and locks the
                  answers. The server re-runs these rules on submit; it does not trust what this
                  screen showed.
                </span>
                {submit.isError && (
                  <span style={{ ...NOTE, color: 'var(--ba)' }}>
                    {submit.error instanceof ApiClientError
                      ? submit.error.message
                      : 'The audit could not be submitted.'}
                  </span>
                )}
                <button
                  type="button"
                  disabled={!data.canSubmit || submit.isPending}
                  title={
                    data.canSubmit
                      ? undefined
                      : `${String(data.blocking.length)} blocking rule(s) must be cleared first`
                  }
                  onClick={() => {
                    submit.mutate();
                  }}
                  style={{
                    height: 34,
                    borderRadius: 'var(--r-md)',
                    border: '1px solid var(--ac)',
                    background: data.canSubmit ? 'var(--ac)' : 'var(--sunk)',
                    color: data.canSubmit ? '#fff' : 'var(--mu)',
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: data.canSubmit ? 'pointer' : 'not-allowed',
                    opacity: data.canSubmit ? 1 : 0.75,
                  }}
                >
                  {submit.isPending ? 'Submitting…' : 'Submit for review'}
                </button>
                {!data.canSubmit && (
                  <span style={{ ...NOTE, color: 'var(--ba)' }}>
                    Refused: {data.blocking.length} blocking{' '}
                    {data.blocking.length === 1 ? 'rule' : 'rules'} listed on the left.
                  </span>
                )}
              </>
            )}
          </div>

          <div style={{ ...CARD, padding: '13px 14px', gap: 10 }}>
            <span style={LABEL}>Approve &amp; close</span>
            <span style={NOTE}>
              Writes the score snapshot <b style={{ color: 'var(--tx)' }}>once</b>. After this the
              audit is a record: a database trigger refuses edits, not just the UI.
            </span>

            {cancelled ? (
              <span style={{ ...NOTE, color: 'var(--mu)' }}>This audit was cancelled.</span>
            ) : closed ? (
              <span style={{ ...NOTE, color: 'var(--ok)' }}>Already approved and frozen.</span>
            ) : !inReview ? (
              <span style={{ ...NOTE, color: 'var(--mu)' }}>
                Available once the audit has been submitted for review.
              </span>
            ) : (
              <>
                {approve.isError && (
                  <span style={{ ...NOTE, color: 'var(--ba)' }}>
                    {approve.error instanceof ApiClientError
                      ? approve.error.message
                      : 'The audit could not be approved.'}
                  </span>
                )}
                <button
                  type="button"
                  disabled={approve.isPending}
                  onClick={() => {
                    approve.mutate();
                  }}
                  style={{
                    height: 34,
                    borderRadius: 'var(--r-md)',
                    border: '1px solid var(--ok)',
                    background: 'var(--ok)',
                    color: '#fff',
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  {approve.isPending ? 'Approving…' : 'Approve & close'}
                </button>
                <span style={NOTE}>
                  The person who submitted an audit cannot approve it. If that is you, the server
                  will refuse and say so — ask another Delivery Manager.
                  {me.data !== undefined && ` You are signed in as ${me.data.displayName}.`}
                </span>
              </>
            )}
          </div>

          {!closed && !cancelled && (
            <div style={{ ...CARD, padding: '13px 14px', gap: 10 }}>
              <span style={LABEL}>Cancel audit</span>
              <span style={NOTE}>
                Abandons this audit. It stays on record as cancelled, can no longer be edited, and
                the project can start a new audit.
              </span>
              {cancel.isError && (
                <span style={{ ...NOTE, color: 'var(--ba)' }}>
                  {cancel.error instanceof ApiClientError
                    ? cancel.error.message
                    : 'The audit could not be cancelled.'}
                </span>
              )}
              <button
                type="button"
                disabled={cancel.isPending}
                onClick={() => {
                  if (window.confirm('Cancel this audit? This cannot be undone.')) {
                    cancel.mutate();
                  }
                }}
                style={{
                  height: 34,
                  borderRadius: 'var(--r-md)',
                  border: '1px solid var(--ba)',
                  background: 'transparent',
                  color: 'var(--ba)',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                {cancel.isPending ? 'Cancelling…' : 'Cancel audit'}
              </button>
            </div>
          )}

          <div style={{ ...CARD, padding: '11px 13px', gap: 6 }}>
            <span style={LABEL}>What is not in the score</span>
            <span style={NOTE}>
              <b style={{ color: 'var(--tx)' }}>{data.projected.notApplicableByContext}</b> items
              left both sides of the sum because the context profile says they do not apply. Waived
              items stayed in the denominator and count as unmet — a waiver records acceptance, not
              achievement.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
